import React, { useCallback, useEffect, useState } from 'react';
import {
  Container, Card, Table, TableBody,
  TableCell, TableContainer, TablePagination,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Box,
  SelectChangeEvent,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { DatePicker, LocalizationProvider } from '@material-ui/lab';
import AdapterDateFns from '@material-ui/lab/AdapterDateFns';
import {
  CommonListHead,
  HeaderBreadcrumbs,
  Page,
  Scrollbar,
  SearchNotFound,
} from '../../../components';
import { ACTIVITY_BREADCRUMBS, ACTIVITY_TABLE_HEAD } from './constants';
import { UserActivityItem } from './UserActivityItem';
import { useStore } from '../../../hooks';
import { useSorting } from '../../../hooks/useSorting';

export const UserActivity: React.FC = observer((): JSX.Element => {
  const { id } = useParams<{ id: string }>();

  const {
    userActivity: { items, meta, fetchUserActivity },
  } = useStore();

  const {
    sort, order, orderBy, onClickSorting,
  } = useSorting();

  const allowedSortFields = ['id', 'action', 'createdAt'];

  const handleClickSorting = (field: string): void => {
    if (!allowedSortFields.includes(field)) return;
    onClickSorting(field);
  };

  const [page, setPage] = useState<number>(0);

  const [selectedAction, setSelectedAction] = useState<string>('');

  const handlePageChange = useCallback(
    (_: unknown, newPage: number): void => {
      setPage(newPage);
    },
    [],
  );

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const handleResetFilters = ():void => {
    setSelectedAction('');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  useEffect(() => {
    // eslint-disable-next-line max-len
    const isValidDate = (date: Date | null): date is Date => date instanceof Date && !Number.isNaN(date.getTime());

    const adjustedEndDate = isValidDate(endDate)
      ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59)
      : undefined;

    const safeStartDate = isValidDate(startDate)
      ? startDate.toISOString()
      : undefined;

    const safeEndDate = adjustedEndDate
      ? adjustedEndDate.toISOString()
      : undefined;

    if (id) {
      fetchUserActivity(+id, {
        page: page + 1,
        sort,
        action: selectedAction || undefined,
        startDate: safeStartDate,
        endDate: safeEndDate,
      });
    }
  }, [id, page, sort, selectedAction, startDate, endDate]);

  return (
    <Page title="User Activity | Minimal-UI">
      <Container maxWidth={false}>
        <HeaderBreadcrumbs heading="User Activity" links={ACTIVITY_BREADCRUMBS} />

        <Card>
          <Box
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            alignItems="center"
            sx={{ gap: 2, p: 2 }}

          >
            <FormControl variant="outlined" size="small" sx={{ width: 250 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={selectedAction}
                onChange={(e: SelectChangeEvent): void => setSelectedAction(e.target.value)}
                label="Action"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="LOGIN">LOGIN</MenuItem>
                <MenuItem value="LOGOUT">LOGOUT</MenuItem>
              </Select>

            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(date): void => setStartDate(date)}
                renderInput={(params): JSX.Element => (
                  <TextField {...params} size="small" sx={{ width: 250 }} />
                )}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(date): void => setEndDate(date)}
                renderInput={(params): JSX.Element => (
                  <TextField {...params} size="small" sx={{ width: 250 }} />
                )}
              />
            </LocalizationProvider>

            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              sx={{ width: 250, height: 40 }}
            >
              Reset
            </Button>

          </Box>

          <TableContainer>
            <Scrollbar>
              <Table>
                <CommonListHead
                  headListData={ACTIVITY_TABLE_HEAD}
                  order={order}
                  orderBy={orderBy}
                  onClick={handleClickSorting}
                />
                <TableBody>
                  {items?.length ? (
                    items.map((item) => (
                      <UserActivityItem key={item.id} data={item} />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          {!!items?.length && (
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={meta.totalItems}
              rowsPerPage={meta.itemsPerPage}
              page={page}
              onPageChange={handlePageChange}
            />
          )}
        </Card>
      </Container>
    </Page>
  );
});
